import { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { RECEIPT_CARD_SMS_NOTICE, RECEIPT_LABELS } from '../../Common/constants/receiptLabels';

dayjs.extend(utc);

const baseUrl = process.env.REACT_APP_API || '';

function formatKST(dateStr, format = 'YYYY.MM.DD. HH:mm:ss') {
    if (!dateStr) return '-';
    return dayjs.utc(dateStr).add(9, 'hour').format(format);
}

// API 응답 → 전표 표시용 구조로 변환
// 백엔드 스펙 확정 시 이 함수만 수정하면 됨
function mapReceiptData(data) {
    const amount = Number(data?.amount || 0);
    const taxableAmount = Math.round(amount / 1.1);
    const vatAmount = amount - taxableAmount;

    return {
        cardBrand: data?.cardName || data?.cardBrand || '-',
        cardNumberMasked: data?.cardNoMasked || data?.cardNumberMasked || '****-****-****-****',
        installmentMonth: data?.installment || data?.type || '일시불',
        approvalNumber: data?.approvalNo || data?.approvalNumber || '-',
        approvalDateTime: formatKST(data?.approvedAt || data?.date),
        cancelDateTime: data?.canceledAt ? formatKST(data.canceledAt) : null,
        isCanceled: !!(data?.cancelStatus || data?.canceledAt),
        pharmacyName: data?.pharmacyName || data?.pharmacy || '-',
        taxableAmount: taxableAmount.toLocaleString('ko-KR') + '원',
        vatAmount: vatAmount.toLocaleString('ko-KR') + '원',
        taxFreeAmount: '0원',
        serviceAmount: '0원',
        totalAmount: amount.toLocaleString('ko-KR') + '원',
        supplierName: data?.supplierInfo?.name || data?.supplierName || '-',
        supplierOwnerName: data?.supplierInfo?.owner_name || data?.supplierOwnerName || '-',
        supplierBusinessNo: data?.supplierInfo?.business_no || data?.supplierBusinessNo || '-',
        supplierContact: data?.supplierInfo?.contact || data?.supplierContact || '-',
        supplierAddress: data?.supplierInfo?.address || data?.supplierAddress || '-',
        merchantName: data?.merchantInfo?.name || data?.merchantName || '-',
        merchantOwnerName: data?.merchantInfo?.boss_name || data?.merchantOwnerName || '-',
        merchantBusinessNo: data?.merchantInfo?.business_no || data?.merchantBusinessNo || '-',
        merchantContact: data?.merchantInfo?.phone || data?.merchantContact || '-',
        merchantAddress: data?.merchantInfo?.address || data?.merchantAddress || '-',
        cardMerchantName: data?.pgMerchantInfo?.name || data?.cardMerchantName || '-',
        cardMerchantOwnerName: data?.pgMerchantInfo?.owner_name || data?.cardMerchantOwnerName || '-',
        cardMerchantBusinessNo: data?.pgMerchantInfo?.business_no || data?.cardMerchantBusinessNo || '-',
        cardMerchantContact: data?.pgMerchantInfo?.contact || data?.pgMerchantInfo?.phone || data?.cardMerchantContact || '-',
        cardMerchantAddress: data?.pgMerchantInfo?.address || data?.cardMerchantAddress || '-',
    };
}

function Row({ label, value, rightAlign = false }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '5px 0', fontSize: '14px' }}>
            <span style={{ color: '#767676', flexShrink: 0 }}>{label}</span>
            <span style={{ fontWeight: 500, textAlign: rightAlign ? 'right' : 'left' }}>{value}</span>
        </div>
    );
}

function SectionTitle({ children }) {
    return <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>{children}</div>;
}

function Divider() {
    return <div style={{ borderBottom: '1px solid #e5e7eb', margin: '14px 0' }} />;
}

export default function AppReceiptPublic() {
    const { token } = useParams();
    const [password, setPassword] = useState('');
    const [phase, setPhase] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
    const [errorMsg, setErrorMsg] = useState('');
    const [receipt, setReceipt] = useState(null);
    const [viewMode, setViewMode] = useState('approval');

    const handleSubmit = async () => {
        if (!password || phase === 'loading') return;
        setPhase('loading');
        try {
            const { data } = await axios.post(
                baseUrl + '/api/terminal/receipt/public',
                { token, password },
                { headers: { 'Content-Type': 'application/json' } }
            );
            if (data?.code === 1000) {
                setReceipt(mapReceiptData(data.data));
                setPhase('success');
            } else {
                setErrorMsg(data?.msg || '비밀번호가 올바르지 않습니다.');
                setPhase('error');
            }
        } catch (err) {
            setErrorMsg(err?.response?.data?.msg || '전표를 불러오는데 실패했습니다.');
            setPhase('error');
        }
    };

    if (phase !== 'success') {
        return (
            <div style={{ minHeight: '100dvh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', boxSizing: 'border-box' }}>
                <div style={{ width: '100%', maxWidth: '360px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827', marginBottom: '4px' }}>{RECEIPT_LABELS.titleKo}</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#6b7280', marginBottom: '8px' }}>{RECEIPT_LABELS.titleEn}</div>
                        <div style={{ fontSize: '14px', color: '#6b7280' }}>카카오톡으로 전달받은 비밀번호를 입력해주세요.</div>
                    </div>
                    <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '28px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>비밀번호</label>
                        <input
                            type="password"
                            inputMode="numeric"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (phase === 'error') setPhase('idle');
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                            placeholder="비밀번호 입력"
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: '10px',
                                border: phase === 'error' ? '1.5px solid #ef4444' : '1px solid #d1d5db',
                                fontSize: '16px',
                                outline: 'none',
                                boxSizing: 'border-box',
                                letterSpacing: '4px',
                                textAlign: 'center',
                            }}
                        />
                        {phase === 'error' && (
                            <div style={{ marginTop: '8px', fontSize: '13px', color: '#ef4444' }}>{errorMsg}</div>
                        )}
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={phase === 'loading' || !password}
                            style={{
                                marginTop: '16px',
                                width: '100%',
                                padding: '13px',
                                borderRadius: '10px',
                                border: 'none',
                                background: phase === 'loading' || !password ? '#93c5fd' : '#0064ff',
                                color: '#fff',
                                fontSize: '15px',
                                fontWeight: 700,
                                cursor: phase === 'loading' || !password ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {phase === 'loading' ? '확인 중...' : '전표 확인'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const r = receipt;
    return (
        <div style={{ minHeight: '100dvh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px 40px', boxSizing: 'border-box' }}>
            <div style={{ width: '100%', maxWidth: '400px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>{RECEIPT_LABELS.titleKo}</div>
                        <div style={{ marginTop: '2px', fontSize: '11px', fontWeight: 600, color: '#6b7280', lineHeight: 1.2 }}>{RECEIPT_LABELS.titleEn}</div>
                    </div>
                    {r.isCanceled && (
                        <div style={{ display: 'flex', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                            <button type="button" onClick={() => setViewMode('approval')} style={{ padding: '5px 12px', fontSize: '13px', fontWeight: 600, border: 'none', background: viewMode === 'approval' ? '#0064ff' : '#fff', color: viewMode === 'approval' ? '#fff' : '#0064ff', cursor: 'pointer' }}>승인</button>
                            <button type="button" onClick={() => setViewMode('cancel')} style={{ padding: '5px 12px', fontSize: '13px', fontWeight: 600, border: 'none', background: viewMode === 'cancel' ? '#b91c1c' : '#fff', color: viewMode === 'cancel' ? '#fff' : '#b91c1c', cursor: 'pointer' }}>취소</button>
                        </div>
                    )}
                </div>

                <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '20px' }}>
                    <SectionTitle>거래정보</SectionTitle>
                    <Row label="카드종류" value={r.cardBrand} />
                    <Row label="카드번호" value={r.cardNumberMasked} />
                    <Row label="할부" value={r.installmentMonth} />
                    <Row label="거래유형" value={viewMode === 'approval' ? '승인' : '취소'} />
                    <Row label="승인번호" value={r.approvalNumber} />
                    <Row label={viewMode === 'approval' ? '거래일시' : '취소일시'} value={viewMode === 'approval' ? r.approvalDateTime : (r.cancelDateTime ?? '-')} />
                    <Row label="거래처명" value={r.pharmacyName} />

                    <Divider />

                    <SectionTitle>공급대가</SectionTitle>
                    <Row label="과세금액" value={r.taxableAmount} />
                    <Row label="부가세" value={r.vatAmount} />
                    <Row label="면세금액" value={r.taxFreeAmount} />
                    <Row label="봉사료" value={r.serviceAmount} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '15px' }}>
                        <span style={{ fontWeight: 700 }}>총 결제금액</span>
                        <span style={{ fontWeight: 700, color: '#f04438', fontSize: '16px' }}>{r.totalAmount}</span>
                    </div>

                    <Divider />

                    <SectionTitle>{RECEIPT_LABELS.storeInfo}</SectionTitle>
                    <Row label="상호명" value={r.merchantName} />
                    <Row label="대표자명" value={r.merchantOwnerName} />
                    <Row label="사업자등록번호" value={r.merchantBusinessNo} />
                    <Row label="이용문의" value={r.merchantContact} />
                    <Row label="사업장소재지" value={r.merchantAddress} rightAlign />

                    <Divider />

                    <SectionTitle>{RECEIPT_LABELS.pspInfo}</SectionTitle>
                    <Row label="상호명" value={r.supplierName} />
                    <Row label="대표자명" value={r.supplierOwnerName} />
                    <Row label="사업자등록번호" value={r.supplierBusinessNo} />
                    <Row label="이용문의" value={r.supplierContact} />
                    <Row label="사업장소재지" value={r.supplierAddress} rightAlign />

                    <Divider />

                    <SectionTitle>{RECEIPT_LABELS.pgInfo}</SectionTitle>
                    <Row label="가맹점명" value={r.cardMerchantName} />
                    <Row label="대표자명" value={r.cardMerchantOwnerName} />
                    <Row label="사업자등록번호" value={r.cardMerchantBusinessNo} />
                    <Row label="이용문의" value={r.cardMerchantContact} />
                    <Row label="사업장소재지" value={r.cardMerchantAddress} rightAlign />

                    <Divider />

                    <div style={{ fontSize: '11px', color: '#9ca3af', lineHeight: 1.6 }}>
                        <div>본 전표는 카카오 알림톡 전송용으로 구성된 내역이며, 결제/정산 시스템의 원장과 차이가 있을 수 있습니다.</div>
                        <div style={{ marginTop: '8px', color: '#666' }}>{RECEIPT_CARD_SMS_NOTICE}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
